import { useState, useMemo } from 'react';
import { CaretUp, CaretDown, CaretUpDown } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

/**
 * Responsive data table.
 * - md+ : a normal <table> (matches the app's existing table styling).
 * - mobile : each row becomes a stacked card (primary title + label/value grid + action footer).
 *
 * columns: [{ key, header, cell:(row)=>node, align?:'right', primary?, actions?, mobileHidden?, mobileBadge?, width?, sortable?, sortValue?:(row)=>any }]
 *   primary      → rendered as the card title on mobile
 *   actions      → rendered right-aligned on desktop & as a full footer on mobile
 *   header === ''→ shown as a badge (no label) in the card title row on mobile
 *   mobileBadge  → keeps the desktop header but renders the cell as a badge in the mobile card title row
 *   width        → tailwind width class applied to the desktop <th> (e.g. 'w-32')
 *   mobileHidden → omitted from the mobile card
 *   sortable     → header becomes a clickable asc/desc toggle; uses sortValue(row) (or row[key]) to compare
 */
function EmptyState({ icon, text, testid }) {
  return (
    <div className="flex flex-col items-center">
      {icon}
      <p className="text-sm text-zinc-500 mt-1" data-testid={testid}>{text}</p>
    </div>
  );
}

const isBlank = (v) => v === null || v === undefined || v === '';

function compareValues(a, b) {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  const an = Number(a), bn = Number(b);
  if (!Number.isNaN(an) && !Number.isNaN(bn) && String(a).trim() !== '' && String(b).trim() !== '') return an - bn;
  return String(a).localeCompare(String(b), 'ja');
}

/**
 * 文字列/数値のセルは2行までにクランプし、全文を title に入れる。
 * 独自JSXを返すセル（名称＋副行など）はそのまま — クランプは
 * 入れ子ブロックに効かず、無言で無効になるため触らない。
 *
 * 注意（Tailwind 3.4）: `line-clamp-*` は display:-webkit-box を当てる。
 * corePlugins の並びは lineClamp(20) → display(21) なので、同じ要素に `block` を
 * 付けると display が後勝ちしてクランプが**無言で無効**になる。クランプを持つ
 * 要素に `block` を付けてはいけない（line-clamp 自体がブロック化する）。
 */
function CellBody({ content }) {
  if (content == null || content === false) return null;
  if (typeof content === 'string' || typeof content === 'number') {
    const text = String(content);
    return <span className="line-clamp-2 break-words" title={text}>{text}</span>;
  }
  return content;
}

export function DataTable({ columns, rows, rowKey, onRowClick, rowTestId, empty, dataTestId, className, defaultSort, isRowSelected, minWidth }) {
  const cols = columns.filter(Boolean);
  const primaryCol = cols.find((c) => c.primary);
  const actionCol = cols.find((c) => c.actions);
  const labelCols = cols.filter((c) => !c.primary && !c.actions && !c.mobileHidden && c.header && !c.mobileBadge);
  const badgeCols = cols.filter((c) => !c.primary && !c.actions && !c.mobileHidden && (!c.header || c.mobileBadge));

  const sampleRow = (rows && rows[0]) || null;
  // A column is sortable when it has a header and is backed by a real field or an explicit sortValue.
  const isSortable = (c) => c.sortable !== false && !c.actions && !!c.header
    && (typeof c.sortValue === 'function' || (sampleRow != null && Object.prototype.hasOwnProperty.call(sampleRow, c.key)));
  const sortableCols = cols.filter(isSortable);

  const [sort, setSort] = useState(defaultSort || { key: null, dir: 'asc' });
  const toggleSort = (key) => setSort((p) => (p.key === key ? { key, dir: p.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  const sortedRows = useMemo(() => {
    if (!sort.key) return rows || [];
    const col = cols.find((c) => c.key === sort.key);
    if (!col) return rows || [];
    const getVal = col.sortValue || ((r) => r[col.key]);
    const mult = sort.dir === 'asc' ? 1 : -1;
    return [...(rows || [])].sort((x, y) => {
      const a = getVal(x); const b = getVal(y);
      const ae = isBlank(a); const be = isBlank(b);
      if (ae && be) return 0;
      if (ae) return 1;     // blanks always last
      if (be) return -1;
      return compareValues(a, b) * mult;
    });
  }, [rows, sort, cols]);

  const isEmpty = !sortedRows || sortedRows.length === 0;

  const SortIcon = ({ col }) => {
    if (!isSortable(col)) return null;
    if (sort.key !== col.key) return <CaretUpDown size={12} className="text-zinc-300" />;
    return sort.dir === 'asc' ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />;
  };

  // 幅の宣言がある表（= 新しい列幅規則に移行済み）だけ table-fixed にする。
  const fixedLayout = cols.some((c) => c.flex);

  return (
    <div className={cn('bg-white rounded-lg border border-zinc-300 shadow-sm overflow-hidden', className)} data-testid={dataTestId}>
      {/* Mobile sort bar */}
      {sortableCols.length > 0 && (
        <div className="md:hidden flex items-center gap-2 px-4 py-2 border-b border-zinc-200 bg-zinc-100">
          <span className="text-xs font-medium text-zinc-500 shrink-0">並び替え</span>
          <select
            data-testid={dataTestId ? `${dataTestId}-sort-select` : 'datatable-sort-select'}
            value={sort.key || ''}
            onChange={(e) => setSort({ key: e.target.value || null, dir: 'asc' })}
            className="flex-1 min-w-0 text-sm rounded-md border border-zinc-200 bg-white px-2 py-1.5 text-zinc-700"
          >
            <option value="">なし</option>
            {sortableCols.map((c) => <option key={c.key} value={c.key}>{c.header}</option>)}
          </select>
          <button
            type="button"
            data-testid={dataTestId ? `${dataTestId}-sort-dir` : 'datatable-sort-dir'}
            disabled={!sort.key}
            onClick={() => setSort((p) => ({ ...p, dir: p.dir === 'asc' ? 'desc' : 'asc' }))}
            className="shrink-0 inline-flex items-center gap-1 text-sm rounded-md border border-zinc-200 bg-white px-2.5 py-1.5 text-zinc-600 disabled:opacity-40"
          >
            {sort.dir === 'asc' ? <CaretUp size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
            {sort.dir === 'asc' ? '昇順' : '降順'}
          </button>
        </div>
      )}

      {/* Desktop / tablet
          列幅の規則（ここが唯一の実装。画面ごとにCSSを当てない）:
          - `width`（Tailwind の w-*）は th と td の両方に付ける。片方だけだと
            table-layout:auto では「弱い最小幅」にしかならず効かない。
          - `flex: true` の列が残り幅を取る（fixed レイアウトでは幅を指定しない列が
            残りを等分するので、flex 列には幅を付けない）。複数あれば分け合う。
            内容の列（品名・説明・ファイル名など）にこれを付ける = 狭められる側にしない。
            `flex` と `width` は排他（max-w-0 が w-* を打ち消して逆に潰れるため、
            flex のときは width を無視する）。下限を持たせたい列は width だけを使う。
          - 幅も flex も無い列は nowrap で内容ぴったりに縮む（バッジ・件数・日付・状態）。
          - 文字列セルは2行でクランプし title に全文を入れる（行が膨らまない）。
          - align:'right' は数値列（tabular-nums）、mono:true は識別子列（font-mono）。 */}
      <div className="hidden md:block overflow-x-auto">
        {/* `flex` を持つ列がある表だけ table-fixed にする。auto レイアウトでは w-* は
            「最小幅の示唆」にすぎず、内容の長い列が余剰幅を全部取ってしまい、内容の列
            （品名など）が潰れる。fixed なら宣言した幅が守られ、残りは flex 列が取る。
            まだこの規則に移行していない表は auto のまま（既存の見た目を変えない）。 */}
        {/* `minWidth`（例 'min-w-[1100px]'）: 固定幅列の合計が広すぎて flex 列が
            読めない幅まで潰れる表だけに付ける。table-fixed は宣言幅を必ず守るので、
            列数が多い表では「潰す」より「上の overflow-x-auto で横スクロールさせる」
            のが正しい。付けていない表は今の見た目のまま（横スクロールを増やさない）。 */}
        <table className={cn('w-full text-sm', fixedLayout && 'table-fixed', minWidth)}>
          <thead>
            <tr className="bg-zinc-100 text-zinc-700 text-xs border-b-2 border-zinc-300">
              {cols.map((c, i) => (
                <th key={c.key || i}
                  className={cn('font-semibold px-4 py-3 whitespace-nowrap', c.align === 'right' ? 'text-right' : 'text-left',
                    !c.flex && (c.width || (c.actions && fixedLayout && 'w-28')))}>
                  {isSortable(c) ? (
                    <button
                      type="button"
                      onClick={() => toggleSort(c.key)}
                      data-testid={dataTestId ? `${dataTestId}-sort-${c.key}` : undefined}
                      className={cn('inline-flex items-center gap-1 select-none transition-colors hover:text-teal-800',
                        sort.key === c.key && 'text-teal-800')}
                    >
                      {c.header}
                      <SortIcon col={c} />
                    </button>
                  ) : c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isEmpty ? (
              <tr><td colSpan={cols.length} className="px-4 py-16 text-center"><EmptyState {...empty} /></td></tr>
            ) : sortedRows.map((r, idx) => (
              <tr
                key={rowKey(r, idx)}
                data-testid={rowTestId ? rowTestId(r, idx) : undefined}
                onClick={onRowClick ? () => onRowClick(r) : undefined}
                className={cn(
                  'border-t border-zinc-200 transition-colors duration-150',
                  isRowSelected?.(r, idx)
                    ? 'bg-teal-50 shadow-[inset_3px_0_0_0_theme(colors.teal.700)] hover:bg-teal-100/70'
                    : 'hover:bg-teal-50/50',
                  onRowClick && 'cursor-pointer')}
              >
                {cols.map((c, i) => (
                  <td
                    key={c.key || i}
                    className={cn('px-4 py-3 overflow-hidden', !c.flex && (c.width || (c.actions && fixedLayout && 'w-28')),
                      c.flex && 'max-w-0',
                      !c.flex && !c.actions && !c.width && 'whitespace-nowrap',
                      c.align === 'right' && 'text-right tabular-nums',
                      c.mono && 'font-mono')}
                    onClick={c.actions ? (e) => e.stopPropagation() : undefined}
                  >
                    {c.actions
                      ? <div className="flex justify-end gap-1 flex-nowrap">{c.cell(r, idx)}</div>
                      : <CellBody content={c.cell(r, idx)} />}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile */}
      <div className="md:hidden">
        {isEmpty ? (
          <div className="px-4 py-14 text-center"><EmptyState {...empty} testid={empty?.testid ? `${empty.testid}-card` : undefined} /></div>
        ) : (
          <div className="divide-y divide-zinc-200">
            {sortedRows.map((r, idx) => (
              <div
                key={rowKey(r, idx)}
                data-testid={rowTestId ? `${rowTestId(r, idx)}-card` : undefined}
                onClick={onRowClick ? () => onRowClick(r) : undefined}
                className={cn('p-4 space-y-3', onRowClick && 'cursor-pointer active:bg-zinc-50')}
              >
                {(primaryCol || badgeCols.length > 0) && (
                  <div className="flex items-start justify-between gap-3">
                    {primaryCol && <div className="min-w-0 flex-1 text-[15px] font-semibold text-zinc-900">{primaryCol.cell(r, idx)}</div>}
                    {badgeCols.length > 0 && (
                      <div className="flex flex-wrap items-center justify-end gap-1 shrink-0">
                        {badgeCols.map((c, i) => <span key={c.key || i}>{c.cell(r, idx)}</span>)}
                      </div>
                    )}
                  </div>
                )}
                {labelCols.length > 0 && (
                  <div className="grid grid-cols-2 gap-x-3 gap-y-2.5">
                    {labelCols.map((c, i) => (
                      <div key={c.key || i} className="flex flex-col gap-0.5 min-w-0">
                        <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">{c.header}</span>
                        <span className="text-sm text-zinc-800 break-words">{c.cell(r, idx)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {actionCol && (
                  <div className="flex items-center gap-2 pt-1" onClick={(e) => e.stopPropagation()}>{actionCol.cell(r, idx)}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
